import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import {
  DefaultButton,
  DefaultPrimaryButton,
  InputError,
  InputWithLabel,
  SelectBox,
} from "../../../components/micro";
import { useDispatch } from "react-redux";
import ConfirmationModel from "../../../components/common/ConfirmationModel";
import Breadcrumb from "../../../components/common/Breadcrumb";
import FileUploadChoose from "../../../components/common/FileUploadChoose";
import {
  createRecipe,
  updateRecipe,
} from "../../../redux/nutrition-recipes/slice";
import { uploadImage } from "../../../redux/uploadFile/slice";
import { DeleteIcon } from "../../../assets/svg";

const AddEditRecipe = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const nutritionData = location?.state?.nutrition;
  const isEditMode = location?.state?.mode;
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [instructionUploading, setInstructionUploading] = useState({});

  const initialValues = useMemo(
    () => ({
      name: nutritionData?.recipeName || "",
      nutritionistName: nutritionData?.nutritionListName || "",
      details: nutritionData?.details || "",
      message: nutritionData?.message || "",
      nutritionistCredentials: nutritionData?.NutritionistCredentials || "",
      tags: nutritionData?.tags?.length ? nutritionData.tags : [""],
      duration: nutritionData?.duration || "",
      servings: nutritionData?.servings || "",
      category: nutritionData?.category || "",
      calories: nutritionData?.calorie || "",
      ingredients: nutritionData?.ingredients?.length
        ? nutritionData.ingredients.map((ing) => ({
            name: ing.ingredientName || "",
            quantity: ing.quantity || "",
            unit: ing.selectUnit || "",
          }))
        : [{ name: "", quantity: "", unit: "" }],
      instructions: nutritionData?.instructions?.length
        ? nutritionData.instructions.map((inst) => ({
            title: inst.title || "",
            details: inst.details || "",
            image: inst.image || null, // URL for preview
            imageKey: inst.imageKey || "", // Key for API payload
          }))
        : [{ title: "", details: "", image: null, imageKey: "" }],
      recipeImage: nutritionData?.recipeImage || "", // URL for preview
      recipeImageKey: nutritionData?.recipeImageKey || "", // Key for API payload
      nutritionListImage: nutritionData?.nutritionListImage || "", // URL for preview
      nutritionListImageKey: nutritionData?.nutritionListImageKey || "", // Key for API payload
    }),
    [nutritionData]
  );

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().nullable().required("Please enter recipe name"),
    nutritionistName: Yup.string()
      .trim()
      .nullable()
      .required("Please enter nutritionist name"),
    details: Yup.string().trim().nullable().required("Please enter details"),
    message: Yup.string().trim().nullable().required("Please enter message"),
    nutritionistCredentials: Yup.string()
      .trim()
      .nullable()
      .required("Please enter Nutritionist Credentials"),
    tags: Yup.array()
      .of(Yup.string().trim().nullable().required("Please enter tag"))
      .min(1, "At least one tag is required"),
    duration: Yup.string().required("Please enter duration"),
    servings: Yup.string().required("Please enter servings"),
    category: Yup.string().required("Please select category"),
    calories: Yup.number()
      .required("Please enter calories")
      .positive("Must be greater than 0"),
    ingredients: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string()
            .trim()
            .nullable()
            .required("Ingredient name is required"),
        })
      )
      .min(1, "Please add at least one ingredient"),

    instructions: Yup.array()
      .of(
        Yup.object().shape({
          title: Yup.string()
            .trim()
            .nullable()
            .required("Instruction title is required"),
          details: Yup.string()
            .trim()
            .nullable()
            .required("Instruction details are required"),
          image: Yup.mixed()
            .nullable()
            .required("Instruction image is required"),
          imageKey: Yup.string().required("Instruction image key is required"),
        })
      )
      .min(1, "Please add at least one instruction"),
    recipeImage: Yup.mixed().required("Recipe image is required"),
    recipeImageKey: Yup.string().required("Recipe image key is required"),
    nutritionListImage: Yup.mixed().required("Nutritionist image is required"),
    nutritionListImageKey: Yup.string().required(
      "Nutritionist image key is required"
    ),
  });

  const handleConfirm = () => {
    setIsLoading(true);

    const payload = {
      recipeName: formValues?.name,
      category: formValues?.category,
      ingredients: formValues?.ingredients?.map((ing) => ({
        ingredientName: ing.name,
        quantity: ing.quantity,
        selectUnit: ing.unit,
      })),
      instructions: formValues?.instructions?.map((inst) => ({
        title: inst.title,
        details: inst.details,
        image: inst?.imageKey || "",
      })),
      recipeImage: formValues?.recipeImageKey || "",
      nutritionListName: formValues?.nutritionistName,
      nutritionListImage: formValues?.nutritionListImageKey || "",
      details: formValues?.details,
      calorie: String(formValues?.calories || ""),
      duration: Number(formValues?.duration),
      servings: Number(formValues?.servings),
      tags: formValues?.tags || [],
      message: formValues?.message || "",
      NutritionistCredentials: formValues?.nutritionistCredentials || "",
    };

    const action = nutritionData?._id
      ? updateRecipe({
          _id: nutritionData._id,
          ...payload,
        })
      : createRecipe(payload);

    dispatch(action)
      .then((res) => {
        if (
          res.type === "createRecipe/fulfilled" ||
          res.type === "updateRecipe/fulfilled"
        ) {
          navigate("/nutrition-recipes");
        }
      })
      .finally(() => {
        setIsLoading(false);
        setShowConfirmationModal(false);
      });
  };

  const handleEditClick = () => setIsEditing(true);
  const handleCancelClick = () => navigate("/nutrition-recipes");

  const handleFileChange = async (
    event,
    setFieldValue,
    fieldType,
    instructionIndex = null
  ) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    setIsUploading(true);

    const tempData = files.map((file, index) => ({
      id: `temp-${Date.now()}-${index}`,
      url: URL.createObjectURL(file),
      type: file.type,
      name: file.name,
      isLoading: true,
      key: null,
    }));

    if (fieldType === "recipe") {
      // Set URL for preview and key for API
      setFieldValue("recipeImage", tempData[0].url);
      setFieldValue("recipeImageKey", "");
    } else if (fieldType === "nutritionist") {
      // Set URL for preview and key for API
      setFieldValue("nutritionListImage", tempData[0].url);
      setFieldValue("nutritionListImageKey", "");
    } else if (fieldType === "instruction") {
      // Set URL for preview and key for API
      setInstructionUploading((prev) => ({
        ...prev,
        [instructionIndex]: true,
      }));
      const temp = tempData[0];
      setFieldValue(`instructions[${instructionIndex}].image`, temp.url);
      setFieldValue(`instructions[${instructionIndex}].imageKey`, "");
    }

    const uploadPromises = files.map((file) => {
      const formData = new FormData();
      formData.append("file", file);
      return dispatch(uploadImage(formData));
    });

    const results = await Promise.allSettled(uploadPromises);

    results.forEach((result, i) => {
      if (
        result.status === "fulfilled" &&
        uploadImage.fulfilled.match(result.value)
      ) {
        const serverUrl = result.value.payload.data.url;
        const serverKey = result.value.payload.data.key;

        if (fieldType === "recipe") {
          // Update URL for preview and key for API
          setFieldValue("recipeImage", serverUrl);
          setFieldValue("recipeImageKey", serverKey);
        } else if (fieldType === "nutritionist") {
          // Update URL for preview and key for API
          setFieldValue("nutritionListImage", serverUrl);
          setFieldValue("nutritionListImageKey", serverKey);
        } else if (fieldType === "instruction") {
          // Update URL for preview and key for API
          setFieldValue(`instructions[${instructionIndex}].image`, serverUrl);
          setFieldValue(
            `instructions[${instructionIndex}].imageKey`,
            serverKey
          );
          setInstructionUploading((prev) => ({
            ...prev,
            [instructionIndex]: false,
          }));
        }
      }
      setIsUploading(false);
    });
  };
  const categoryOptions = [
    { value: "healthy_snacks", label: "Healthy Snacks" },
    { value: "low_carbs", label: "Low carbs" },
    { value: "high_protein", label: "High protein" },
    { value: "mediterranean", label: "Mediterranean" },
    { value: "vegetarian", label: "Vegetarian" },
  ];

  return (
    <>
      <div className="mb-5">
        <h6 className="font-Montserrat font-medium text-xl mb-3">
          {isEditMode == "edit" ? "Edit Recipe " : "Create Recipe"}
        </h6>
        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbActive={
            isEditMode === "edit" ? "Edit Recipe" : "Create Recipe"
          }
          breadCrumbParent3={"Recipe"}
          breadCrumbParent2={" Nutrition"}
          breadCrumbTitleKey={"/"}
          classes=""
        />
      </div>
      <div>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            setFormValues(values);
            setShowConfirmationModal(true);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-6 mb-4 w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6">
                  {/* Recipe Name */}
                  <InputWithLabel
                    idFromName="name"
                    label="Recipe Name*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    errors={errors.name && touched.name}
                    className={`${
                      errors?.name && touched?.name
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                    errorsText={errors.name}
                    disabled={isEditMode === "edit" && !isEditing}
                  />
                  {/* Nutritionist Name */}
                  <InputWithLabel
                    idFromName="nutritionistName"
                    label="Nutritionist Name*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.nutritionistName}
                    errors={errors.nutritionistName && touched.nutritionistName}
                    className={`${
                      errors?.nutritionistName && touched?.nutritionistName
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                    errorsText={errors.nutritionistName}
                    disabled={isEditMode === "edit" && !isEditing}
                  />

                  {/* Details */}
                  <InputWithLabel
                    idFromName="details"
                    label="Details*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.details}
                    errorsText={errors.details}
                    errors={errors.details && touched.details}
                    className={`${
                      errors?.details && touched?.details
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                    disabled={isEditMode === "edit" && !isEditing}
                  />

                  {/* Duration */}
                  <InputWithLabel
                    idFromName="duration"
                    label="Duration*"
                    type="number"
                    onFocus={(e) =>
                      e.target.addEventListener(
                        "wheel",
                        function (e) {
                          e.preventDefault();
                        },
                        { passive: false }
                      )
                    }
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.duration}
                    errorsText={errors.duration}
                    errors={errors.duration && touched.duration}
                    className={`${
                      errors?.duration && touched?.duration
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                    disabled={isEditMode === "edit" && !isEditing}
                  />
                  {/* Servings */}
                  <InputWithLabel
                    idFromName="servings"
                    label="Servings*"
                    type="number"
                    onFocus={(e) =>
                      e.target.addEventListener(
                        "wheel",
                        function (e) {
                          e.preventDefault();
                        },
                        { passive: false }
                      )
                    }
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.servings}
                    errorsText={errors.servings}
                    errors={errors.servings && touched.servings}
                    className={`${
                      errors?.servings && touched?.servings
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                    disabled={isEditMode === "edit" && !isEditing}
                  />

                  {/* Category */}
                  <SelectBox
                    name="category"
                    label="Category"
                    placeholder={"Select Category*"}
                    options={categoryOptions}
                    value={
                      categoryOptions.find(
                        (opt) => opt.value === values.category
                      ) || null
                    }
                    onChange={(option) => {
                      setFieldValue("category", option?.value ?? "");
                    }}
                    isDisabled={isEditMode === "edit" && !isEditing}
                    errors={errors.category && touched.category}
                    errorsText={errors.category}
                  />

                  {/* final note */}
                  <InputWithLabel
                    idFromName="message"
                    label="Final note*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.message}
                    errorsText={errors.message}
                    errors={errors.message && touched.message}
                    className={`${
                      errors?.message && touched?.message
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                    disabled={isEditMode === "edit" && !isEditing}
                  />
                  {/* NutritionistCredentials */}
                  <InputWithLabel
                    idFromName="nutritionistCredentials"
                    label="Nutritionist Credentials*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.nutritionistCredentials}
                    errors={
                      errors.nutritionistCredentials &&
                      touched.nutritionistCredentials
                    }
                    className={`${
                      errors?.nutritionistCredentials &&
                      touched?.nutritionistCredentials
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                    errorsText={errors.nutritionistCredentials}
                    disabled={isEditMode === "edit" && !isEditing}
                  />
                  <InputWithLabel
                    idFromName="calories"
                    name="calories"
                    label="Calories*"
                    type="number"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.calories}
                    errorsText={errors.calories}
                    disabled={isEditMode === "edit" && !isEditing}
                    errors={errors.calories && touched.calories}
                    className={`${
                      errors?.calories && touched?.calories
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                    onFocus={(e) =>
                      e.target.addEventListener(
                        "wheel",
                        function (e) {
                          e.preventDefault();
                        },
                        { passive: false }
                      )
                    }
                  />
                  <div className="col-span-2">
                    <FieldArray
                      name="tags"
                      render={(arrayHelpers) => (
                        <div className="space-y-3">
                          {values.tags.length > 0
                            ? values.tags.map((tag, index) => (
                                <div
                                  key={index}
                                  className="flex items-center gap-2 rounded-md"
                                >
                                  {/* Tag Input */}
                                  <div className="w-full">
                                    <InputWithLabel
                                      name={`tags[${index}]`}
                                      label={`Add Tag*`}
                                      value={tag}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      errorsText={errors?.tags?.[index]}
                                      errors={
                                        errors?.tags?.[index] &&
                                        touched?.tags?.[index]
                                      }
                                      className={`${
                                        errors?.tags?.[index] &&
                                        touched?.tags?.[index]
                                          ? `border border-red`
                                          : `border border-borderTheme/80 focus-within:border-themeBtn `
                                      } w-full `}
                                      disabled={
                                        isEditMode === "edit" && !isEditing
                                      }
                                    />
                                  </div>

                                  {/* Remove Button – only show if more than 1 field */}
                                  {values.tags.length > 1 && (
                                    <button
                                      type="button"
                                      className="text-red-500 hover:text-red-700 disabled:opacity-50"
                                      onClick={() => arrayHelpers.remove(index)}
                                      disabled={
                                        isEditMode === "edit" && !isEditing
                                      }
                                    >
                                      <DeleteIcon />
                                    </button>
                                  )}
                                </div>
                              ))
                            : arrayHelpers.push("")}

                          {/* Add New Tag Button */}
                          <div className="flex justify-center mt-3">
                            <button
                              type="button"
                              className={`px-4 py-2 bg-white rounded-md border transition ${
                                isEditMode === "edit" && !isEditing
                                  ? "text-textGrey border border-dashed"
                                  : "text-themeBtn border-themeBtn hover:bg-themeBtn hover:text-white"
                              }`}
                              disabled={isEditMode === "edit" && !isEditing}
                              onClick={() => arrayHelpers.push("")}
                            >
                              + Add Tag
                            </button>
                          </div>
                        </div>
                      )}
                    />
                  </div>

                  {/* Recipe Image Upload */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2 text-gray-dark font-Proxima">
                      Recipe Image*
                    </label>
                    <div>
                      <FileUploadChoose
                        className={`border border-borderTheme/80 transition duration-300 ease-in-out ${
                          isEditMode === "edit" && !isEditing
                            ? "text-textGrey border border-dashed "
                            : "border border-borderTheme/80"
                        } ${
                          errors.recipeImage && touched.recipeImage
                            ? "border-red"
                            : ""
                        }`}
                        accept="image/*"
                        filemsg="Upload JPG, PNG, JPEG file"
                        disabled={isEditMode === "edit" && !isEditing}
                        handleFileChange={(e) =>
                          handleFileChange(e, setFieldValue, "recipe")
                        }
                        uploadedFiles={
                          values.recipeImageKey // show delete icon only when upload API success
                            ? [{ url: values.recipeImage }]
                            : []
                        }
                        onDelete={() => {
                          setFieldValue("recipeImage", "");
                          setFieldValue("recipeImageKey", ""); //  reset success flag
                        }}
                      />
                    </div>
                    {errors.recipeImage && touched.recipeImage ? (
                      <InputError errorTitle={errors.recipeImage} />
                    ) : null}
                  </div>
                  {/* Nutritionist Image Upload */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2 text-gray-dark font-Proxima">
                      Nutritionist Image*
                    </label>
                    <FileUploadChoose
                      className={`  transition duration-300 ease-in-out  ${
                        isEditMode === "edit" && !isEditing
                          ? "text-textGrey border border-dashed"
                          : "border border-borderTheme/80"
                      }  ${
                        errors.nutritionListImage && touched.nutritionListImage
                          ? "border-red"
                          : ""
                      }`}
                      disabled={isEditMode === "edit" && !isEditing}
                      accept="image/*"
                      filemsg="Upload JPG, PNG, JPEG file"
                      handleFileChange={(e) =>
                        handleFileChange(e, setFieldValue, "nutritionist")
                      }
                      uploadedFiles={
                        values.nutritionListImageKey // ← Only show delete icon if API success
                          ? [{ url: values.nutritionListImage }]
                          : []
                      }
                      onDelete={() => {
                        setFieldValue("nutritionListImage", "");
                        setFieldValue("nutritionListImageKey", ""); // ← Reset success flag
                      }}
                    />

                    {errors.nutritionListImage && touched.nutritionListImage ? (
                      <InputError errorTitle={errors.nutritionListImage} />
                    ) : null}
                  </div>
                </div>

                {/* Ingredients */}
                <div className="col-span-2 mb-4 w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6">
                  <label className="block text-sm font-medium mb-2 text-gray-dark font-Proxima">
                    Ingredients*
                  </label>

                  <FieldArray
                    name="ingredients"
                    render={(arrayHelpers) => (
                      <div>
                        {values.ingredients.map((ingredient, index) => (
                          <div
                            key={index}
                            className="relative border border-borderTheme/80 p-5 rounded-md mb-4"
                          >
                            {values.ingredients.length > 1 && (
                              <button
                                type="button"
                                className="absolute top-2 right-0 text-red-500 hover:text-red-700 disabled:opacity-50"
                                onClick={() => arrayHelpers.remove(index)}
                                disabled={isEditMode === "edit" && !isEditing}
                              >
                                <DeleteIcon />
                              </button>
                            )}

                            <div className="grid grid-cols-3 gap-4">
                              {/* Ingredient Name */}
                              <div>
                                <InputWithLabel
                                  name={`ingredients.${index}.name`}
                                  label="Ingredient Name*"
                                  value={ingredient.name}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  type="textarea"
                                  rows={1}
                                  errorsText={
                                    errors?.ingredients?.[index]?.name
                                  }
                                  errors={
                                    errors.ingredients?.[index]?.name &&
                                    touched.ingredients?.[index]?.name
                                  }
                                  className={`${
                                    errors?.ingredients?.[index]?.name &&
                                    touched?.ingredients?.[index]?.name
                                      ? `border border-red`
                                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                                  } w-full `}
                                  disabled={isEditMode === "edit" && !isEditing}
                                />
                              </div>
                              {/* Quantity */}
                              <div>
                                <InputWithLabel
                                  name={`ingredients.${index}.quantity`}
                                  label="Quantity"
                                  value={ingredient.quantity}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  disabled={isEditMode === "edit" && !isEditing}
                                  className={`${
                                    isEditMode === "edit" && !isEditing
                                      ? "text-textGrey border border-dashed"
                                      : "text-black border hover:border-themeBtn"
                                  } border border-borderTheme/80 transition duration-300 ease-in-out`}
                                />
                              </div>
                              {/* Unit */}
                              <div>
                                <InputWithLabel
                                  name={`ingredients.${index}.unit`}
                                  label="Unit"
                                  value={ingredient.unit}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  disabled={isEditMode === "edit" && !isEditing}
                                  className={`${
                                    isEditMode === "edit" && !isEditing
                                      ? "text-textGrey border border-dashed"
                                      : "text-black border hover:border-themeBtn"
                                  } border border-borderTheme/80 focus-within:border-themeBtn transition duration-300 ease-in-out `}
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* Add Ingredient Button */}
                        <div className="flex justify-center mt-3">
                          <button
                            type="button"
                            className={`px-4 py-2 bg-white rounded-md border items-center transition ${
                              isEditMode === "edit" && !isEditing
                                ? "text-textGrey border border-dashed"
                                : "text-themeBtn border-themeBtn hover:bg-themeBtn hover:text-white"
                            }`}
                            disabled={isEditMode === "edit" && !isEditing}
                            onClick={() =>
                              arrayHelpers.push({
                                name: "",
                                quantity: "",
                                unit: "",
                              })
                            }
                          >
                            + Add Ingredient
                          </button>
                        </div>
                      </div>
                    )}
                  />
                </div>
                {/* Instructions */}
                <div className="col-span-2 mb-4 w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6">
                  <label className="block text-sm font-medium mb-2 text-gray-dark font-Proxima">
                    Instructions*
                  </label>
                  <FieldArray
                    name="instructions"
                    render={(arrayHelpers) => (
                      <div className="space-y-6">
                        {values.instructions.map((item, index) => (
                          <div>
                            <p className="font-semibold text-sm mb-3">{`Step ${
                              index + 1
                            }`}</p>
                            <div
                              key={index}
                              className="grid grid-cols-2 gap-6 p-4 border border-borderTheme/80 rounded-md relative mt-0"
                            >
                              <div className="flex flex-col gap-6">
                                {/* Instruction Title */}
                                <InputWithLabel
                                  idFromName={`instructions[${index}].title`}
                                  label="Instruction Title*"
                                  type="text"
                                  onChange={handleChange}
                                  disabled={isEditMode === "edit" && !isEditing}
                                  onBlur={handleBlur}
                                  value={item.title || ""}
                                  errors={
                                    errors.instructions?.[index]?.title &&
                                    touched.instructions?.[index]?.title
                                  }
                                  className={`${
                                    errors?.instructions?.[index]?.title &&
                                    touched?.instructions?.[index]?.title
                                      ? `border border-red`
                                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                                  } w-full `}
                                  errorsText={
                                    errors.instructions?.[index]?.title
                                  }
                                />
                                {/* Instruction Details */}
                                <InputWithLabel
                                  idFromName={`instructions[${index}].details`}
                                  label="Instruction Details*"
                                  rows={6}
                                  type="textarea"
                                  onChange={handleChange}
                                  disabled={isEditMode === "edit" && !isEditing}
                                  onBlur={handleBlur}
                                  value={item.details || ""}
                                  errors={
                                    errors.instructions?.[index]?.details &&
                                    touched.instructions?.[index]?.details
                                  }
                                  className={`${
                                    errors?.instructions?.[index]?.details &&
                                    touched?.instructions?.[index]?.details
                                      ? `border border-red`
                                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                                  } w-full `}
                                  errorsText={
                                    errors.instructions?.[index]?.details
                                  }
                                />
                              </div>
                              <div className="">
                                {item.image ? (
                                  <div className="relative inline-block w-52 h-40">
                                    <img
                                      src={
                                        typeof item.image === "string"
                                          ? item.image
                                          : item.image.url
                                      }
                                      alt="instruction"
                                      className="w-52 h-52 object-cover rounded-md"
                                    />

                                    {/* Uploading Loader */}
                                    {instructionUploading?.[index] && (
                                      <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-md w-52 h-52">
                                        <div className="w-10 h-10 border-4 border-themeBtn border-t-transparent rounded-full animate-spin"></div>
                                      </div>
                                    )}

                                    {/* DELETE ICON — show only when upload SUCCESS */}
                                    {item.imageKey &&
                                      !instructionUploading?.[index] && (
                                        <button
                                          type="button"
                                          className="absolute -top-2 -right-2 bg-white text-black rounded-full w-6 h-6 flex items-center justify-center text-base shadow-md transition"
                                          disabled={
                                            isEditMode === "edit" && !isEditing
                                          }
                                          onClick={() => {
                                            const updated = [
                                              ...values.instructions,
                                            ];
                                            updated[index].image = null;
                                            updated[index].imageKey = "";
                                            setFieldValue(
                                              "instructions",
                                              updated
                                            );
                                          }}
                                        >
                                          <DeleteIcon />
                                        </button>
                                      )}
                                  </div>
                                ) : (
                                  <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-dark font-Proxima">
                                      Instruction Image*
                                    </label>

                                    <FileUploadChoose
                                      className="border border-borderTheme/80 transition duration-300 ease-in-out flex items-center justify-center rounded-md"
                                      handleFileChange={(event) =>
                                        handleFileChange(
                                          event,
                                          setFieldValue,
                                          "instruction",
                                          index
                                        )
                                      }
                                      disabled={
                                        isEditMode === "edit" && !isEditing
                                      }
                                      uploadedFiles={
                                        values.instructions[index]?.image
                                          ? [
                                              {
                                                url: values.instructions[index]
                                                  .image,
                                              },
                                            ]
                                          : []
                                      }
                                      accept="image/*"
                                      filemsg="Upload JPG, PNG, JPEG file"
                                    />
                                  </div>
                                )}

                                {errors.instructions?.[index]?.imageKey &&
                                  touched.instructions?.[index]?.imageKey && (
                                    <InputError
                                      errorTitle={
                                        errors.instructions[index].imageKey
                                      }
                                    />
                                  )}
                              </div>

                              {/* Remove Step Button */}
                              {values.instructions.length > 1 && (
                                <button
                                  type="button"
                                  className="absolute -top-2 -right-7 text-black rounded-full px-2 py-1 text-xl"
                                  onClick={() => arrayHelpers.remove(index)}
                                  disabled={isEditMode === "edit" && !isEditing}
                                >
                                  <DeleteIcon />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}

                        {/* Add New Instruction Button */}
                        <div className="flex justify-center">
                          <button
                            type="button"
                            className={`px-4 py-2 bg-white rounded-md border items-center transition ${
                              isEditMode === "edit" && !isEditing
                                ? "text-textGrey border border-dashed"
                                : "text-themeBtn border-themeBtn hover:bg-themeBtn hover:text-white"
                            }`}
                            disabled={isEditMode === "edit" && !isEditing}
                            onClick={() =>
                              arrayHelpers.push({
                                title: "",
                                details: "",
                                image: null,
                                imageKey: "",
                              })
                            }
                          >
                            + Add Instruction
                          </button>
                        </div>
                      </div>
                    )}
                  />
                  {typeof errors.instructions === "string" &&
                    touched.instructions && (
                      <InputError errorTitle={errors.instructions} />
                    )}
                </div>

                {/* Submit button */}
                <div className="flex justify-end gap-3 mt-3">
                  <DefaultPrimaryButton
                    type="button"
                    value={isEditMode === "edit" ? "Back" : "Cancel"}
                    onClick={handleCancelClick}
                  />
                  {isEditMode === "edit" ? (
                    isEditing ? (
                      <>
                        <DefaultButton
                          type="submit"
                          value="Save"
                          onClick={(e) => {}}
                        />
                      </>
                    ) : (
                      <DefaultButton
                        value="Edit"
                        onClick={handleEditClick}
                        type="submit"
                      />
                    )
                  ) : (
                    <>
                      <DefaultButton type="submit" value="confirm" />
                    </>
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
      {showConfirmationModal && (
        <div>
          <ConfirmationModel
            confirmText={isEditMode === "edit" ? "Edit" : "Create"}
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={() => handleConfirm()}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default AddEditRecipe;
